import React, { useEffect, useState } from 'react';
import EnhancedButton from '@components/button/EnhancedButton';
import DocumentWidget from '@components/widgets/document-widget/DocumentWidget';
import { IConfirmation } from '@redux/confirmation/types';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { IAbstractRecord } from '@models';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getProductionDocuments, regenerateDocuments } from '../../queries';
import { useParams } from 'react-router-dom';
import { GetDocumentsData } from '../../functions';
import { IDocumentsDetails } from '../..';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import AddDocumentDrawer from './AddDocumentDrawer';
import { IFileCardProps } from '@models/file';
import { addFileToDocumentWidgetFiles } from './utils';
import { useTabsLayoutStyles } from '@styles/sharedStyles';
import Loader from '@components/loader/Loader';
import { CDN_URL } from '@utils';

interface IDocumentsTab {
  data: IAbstractRecord;
  isGroupPolicy?: boolean;
}

const DocumentsTab: React.FC<IDocumentsTab> = ({ data, isGroupPolicy }) => {
  const { classes } = useTabsLayoutStyles();
  const { id: entityId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);

  const [getDocumentsDetailsLazy, { loading }] = useLazyQuery(
    getProductionDocuments()
  );
  const [regenerateDocumentsMutation] = useMutation(regenerateDocuments());

  const [documentsData, setDocumentsData] = useState<IDocumentsDetails>();
  const [addDocumentDrawerOpen, setAddDocumentDrawerOpen] =
    useState<boolean>(false);

  const loadDocuments = async () => {
    const { data } = await getDocumentsDetailsLazy({
      variables: { id: entityId },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    const summaryData = GetDocumentsData(data);

    setDocumentsData(summaryData);
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const documentWidgetFiles: IFileCardProps[] = [];
  const additionalDocumentWidgetFiles: IFileCardProps[] = [];

  const showRegenrateDocumentsAction =
    !isGroupPolicy &&
    user.info.businessUser_AllowPolicyCorrection &&
    data.RegenerateDocuments &&
    ['43', '51', '50', '4'].includes(data?.LineId?.ExternalCode);

  if (documentsData) {
    if (
      documentsData.regeneratedDocuments &&
      documentsData.regeneratedDocuments.length > 0
    ) {
      documentsData.regeneratedDocuments.forEach((policyDocument) => {
        documentWidgetFiles.push(
          addFileToDocumentWidgetFiles(
            policyDocument.file,
            'Document',
            'Production-PolicyDocument',
            true,
            policyDocument.createdOn || '',
            policyDocument.version,
            policyDocument.code
          )
        );
      });
    }

    if (
      documentsData.additionalDocuments &&
      documentsData.additionalDocuments.length > 0
    ) {
      documentsData.additionalDocuments.forEach((policyDocument) => {
        additionalDocumentWidgetFiles.push(
          addFileToDocumentWidgetFiles(
            policyDocument.file,
            'Document',
            'Production-PolicyDocument',
            true,
            policyDocument.createdOn,
            policyDocument.version,
            policyDocument.code
          )
        );
      });
    }
  }

  return loading ? (
    <Loader />
  ) : (
    <section className={classes.tabContents}>
      <DocumentWidget
        title="Policy Documents"
        cdnUrl={CDN_URL}
        files={documentWidgetFiles}
        actions={
          showRegenrateDocumentsAction && (
            <div>
              <EnhancedButton
                onClick={() => {
                  const confirmation: IConfirmation = {
                    open: true,
                    title: 'Confirmation',
                    message:
                      'Are you sure you want to regenerate the documents?',
                    callback: async () => {
                      if (entityId) {
                        await regenerateDocumentsMutation({
                          variables: {
                            entityId: entityId,
                          },
                        }).then((response) => {
                          if (response.data) {
                            toast.success(
                              <ToastSuccessMessage>
                                Documents regenerated successfully
                              </ToastSuccessMessage>
                            );
                            loadDocuments();
                          } else if (response.errors) {
                            toast.error(
                              <ToastErrorMessage>
                                {getError(response)}
                              </ToastErrorMessage>
                            );
                          }
                        });
                      }
                    },
                    submitButtonText: 'Yes',
                    cancelButtonText: 'No',
                  };
                  dispatch(OpenConfirmationAction(confirmation));
                }}
              >
                Regenerate Documents
              </EnhancedButton>
            </div>
          )
        }
      />
      <DocumentWidget
        title="Additional Documents"
        cdnUrl={CDN_URL}
        files={additionalDocumentWidgetFiles}
        disabledAddDocumentAction={false}
        actions={
          <EnhancedButton
            type="button"
            style={{ display: 'inline' }}
            onClick={() => {
              setAddDocumentDrawerOpen(true);
            }}
            disabled={addDocumentDrawerOpen}
          >
            Add Document
          </EnhancedButton>
        }
        policyEntityId={entityId}
      />
      {addDocumentDrawerOpen && (
        <AddDocumentDrawer
          open={addDocumentDrawerOpen}
          onClose={() => {
            setAddDocumentDrawerOpen(false);
          }}
          onSuccess={() => {
            setAddDocumentDrawerOpen(false);
            loadDocuments();
          }}
          entityId={entityId}
        />
      )}
    </section>
  );
};

export default DocumentsTab;
